import { Show, useShowController } from "react-admin";
import { SectionTitle } from "features/SectionTitle";
import PersonalData from "./PersonalData";
import UniversityData from "./UniversityData";
import HealthCareData from "./HealthCareData";
import EducationData from "./EducationData";
import SportActivitiesData from "./Activities/SportActivitiesData";
import { tenants } from "utils/tenants";
import { CitizenshipIds } from "utils/constants";
import ForeignLanguagesActivitiesData from "./Activities/ForeignLanguagesActivitiesData";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { settings } from "app";
import HistoryData from "./HistoryData";
import dataProvider from "server/dataProvider";
import { graduationRequests } from "api/graduationApi";
import { studentRequests } from "api/studentApi";
import { useCallback, useEffect, useState } from "react";
import StudentAvatar from "./StudentAvatar";
import StudentRecordToolbar from "./StudentRecordToolbar";
import BackButtonToolbar from "features/BackButtonToolbar";
import AddressData from "./AddressData";
import PreviousEducationData from "./PreviousEducationData";
import PersonalDocumentsData from "./PersonalDocumentsData";
import PaymentHistory from "./PaymentHistory";
import SemesterFee from "./SemesterFee";
import Preferences from "./Preferences";
import PracticesData from "./Practices/PracticesData";

const ShowStudents = () => {
  const { record } = useShowController();
  const [historyOfChangedStatuses, setHistoryOfChangedStatuses] = useState([]);
  const [internshipStudentStatus, setInternshipStudentStatus] = useState(null);
  const isForeigner =
    record?.userInfo?.citizenship === CitizenshipIds.EuForeigners ||
    record?.userInfo?.citizenship === CitizenshipIds.NotEuForeigners;
  const fetchInternshipStudentStatus = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        graduationRequests.getInternshipStudentStatus(record?.id),
        null,
        "GET",
      );
      setInternshipStudentStatus(data.status);
    } catch (err) {
      console.error(err);
    }
  }, [record?.id]);

  const fetchHistory = useCallback(async () => {
    if (!record?.id) return;
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudentStatusHistory(record?.id),
        null,
        "GET",
      );
      setHistoryOfChangedStatuses(data || []);
    } catch (err) {
      setHistoryOfChangedStatuses([]);
      console.error(err.message);
    }
  }, [record?.id]);

  useEffect(() => {
    if (!record?.id) return;
    Promise.all([fetchInternshipStudentStatus(), fetchHistory()]);
  }, [record?.id, fetchInternshipStudentStatus, fetchHistory]);

  return (
    <Show
      title="Детайли за студент"
      actions={
        settings.tenant === tenants.MU ? (
          <StudentRecordToolbar
            educationId={record?.id}
            internshipStudentStatus={internshipStudentStatus}
            record={record}
            fetchInternshipStudentStatus={fetchInternshipStudentStatus}
            fetchHistory={fetchHistory}
          />
        ) : (
          <BackButtonToolbar />
        )
      }
    >
      <StudentAvatar
        educationId={record?.id}
        studentId={record?.studentId}
        facultyNumber={record?.facultyNumber}
        ean={record?.ean}
        educationStatus={record?.educationStatus}
      />
      <SectionTitle
        paddingY={2}
        title="Лични данни на студента"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <PersonalData record={record} />

      <SectionTitle
        paddingY={2}
        title="Университетски данни на студента"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <UniversityData
        record={record}
        fetchHistory={fetchHistory}
      />
      <SectionTitle
        paddingY={2}
        title="Здравен статус на студента"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <HealthCareData
        record={record}
        isForeigner={isForeigner}
      />
      <SectionTitle
        title="Данни за образование"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <EducationData record={record} />

      {record?.curriculumId ? (
        <SecuredComponent permission={permissions.canPreviewStudentEducationInfo}>
          <SectionTitle
            paddingY={2}
            title="Учебни практики"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <PracticesData
            educationId={record?.id}
            curriculumId={record?.curriculumId}
          />
        </SecuredComponent>
      ) : null}
      <SecuredComponent permission={permissions.canPreviewStudentSemesterFees}>
        <SectionTitle
          paddingY={2}
          title="Семестриална такса"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <SemesterFee educationId={record?.id} />
      </SecuredComponent>
      <SecuredComponent permission={permissions.canPreviewUserDebtHistory}>
        <SectionTitle
          paddingY={2}
          title="История на плащанията"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <PaymentHistory userUId={record?.userInfo?.userUId} />
      </SecuredComponent>
      {record?.preferences?.some(p => p.preferenceId > 0) && (
        <SecuredComponent permission={permissions.canPreviewStudentPreferenceAdditionalData}>
          <SectionTitle
            paddingY={2}
            title="Преференции"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <Preferences
            record={record}
            isForeigner={isForeigner}
          />
        </SecuredComponent>
      )}
      <SectionTitle
        paddingY={2}
        title="История на сменени статуси"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <HistoryData historyOfChangedStatuses={historyOfChangedStatuses} />
      {isForeigner ? (
        <SecuredComponent permission={permissions.canPreviewStudentPersonalDocuments}>
          <SectionTitle
            paddingY={2}
            title="Прикачени документи за самоличност"
            textAlign="center"
            sx={{ marginTop: "2rem" }}
          />
          <PersonalDocumentsData record={record} />
        </SecuredComponent>
      ) : null}
      <SecuredComponent permission={permissions.canPreviewUsers}>
        <SectionTitle
          paddingY={2}
          title="Адреси на студента"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <AddressData record={record} />
      </SecuredComponent>
      <SecuredComponent permission={permissions.canPreviewStudentPreviousEducation}>
        <SectionTitle
          paddingY={2}
          title="Предходно образование на студента"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <PreviousEducationData record={record} />
      </SecuredComponent>
      {record?.studentId && settings.tenant === tenants.MGU && (
        <>
          <SecuredComponent permission={permissions.canPreviewStudentsForeignLanguages}>
            <SectionTitle
              paddingY={2}
              title="Занятия чужди езици"
              textAlign="center"
              sx={{ marginTop: "2rem" }}
            />
            <ForeignLanguagesActivitiesData educationId={record?.id} />
          </SecuredComponent>
          <SecuredComponent permission={permissions.canPreviewStudentsSportActivities}>
            <SectionTitle
              paddingY={2}
              title="Занятия спорт"
              textAlign="center"
              sx={{ marginTop: "2rem" }}
            />
            <SportActivitiesData educationId={record?.id} />
          </SecuredComponent>
        </>
      )}
    </Show>
  );
};
export default ShowStudents;
